import { Checkbox } from "../../ui/checkbox";
import { Input } from "../../ui/input";
import { BulletList } from "../../common";
import { useSession } from "../../../lib/session";
import { withKeys } from "../../../lib/keys";

const PR = { STAT: "cc-chip-critical", Urgent: "cc-chip-warn", Routine: "cc-chip-ok" };

export const TriageCard = ({ triage, summary }) => (
  <div className="grid md:grid-cols-[220px_1fr] border rounded-md bg-card" data-testid="triage-result">
    <div className="p-5 border-b md:border-b-0 md:border-r bg-[var(--cc-red)] text-white rounded-t-md md:rounded-l-md md:rounded-tr-none">
      <div className="cc-label text-white/80">Triage</div>
      <div className="font-heading text-xl font-semibold mt-1" data-testid="triage-category">{triage.category}</div>
      <div className="text-sm mt-1">ICU: {triage.icu_admission}</div>
    </div>
    <div className="p-5 text-sm">{triage.rationale}{summary && <p className="text-muted-foreground mt-2">{summary}</p>}</div>
  </div>
);

export const ImmediateActions = ({ items }) => (
  <div className="border-l-4 border-[var(--cc-red)] bg-card p-4 text-sm" data-testid="immediate-actions">
    <div className="cc-label text-[var(--cc-red)] mb-1">Immediate actions</div>
    <BulletList items={items} className="list-disc pl-5 space-y-0.5" />
  </div>
);

export const SyndromeBanner = ({ syndrome }) => (
  <div className="border rounded-md bg-card p-5 flex flex-wrap items-center gap-3" data-testid="syndrome">
    <span className="cc-label text-[var(--cc-blue)]">Presenting syndrome</span>
    <span className="font-heading text-lg font-semibold" data-testid="syndrome-name">{syndrome.name}</span>
    {syndrome.ref && <span className="text-xs text-muted-foreground ml-auto">{syndrome.ref}</span>}
  </div>
);

const AdditionalQuestion = ({ q, i }) => {
  const { state, set } = useSession();
  return (
    <div className="p-4 grid md:grid-cols-[1fr_300px] gap-3 items-center" data-testid={`additional-q-${i}`}>
      <div>
        <div className="flex items-center gap-2">
          <span className={`cc-chip ${q.ask === "Physician" ? "cc-chip-warn" : "cc-chip-ok"}`}>{q.ask}</span>
          <span className="font-medium">{q.question}</span>
        </div>
        {q.purpose && <div className="text-xs text-muted-foreground mt-1">{q.purpose}</div>}
      </div>
      <Input data-testid={`additional-q-${i}-answer`} className="cc-input" placeholder="Answer / finding"
        value={state.extraAnswers?.[q.id] || ""} onChange={(e) => set("extraAnswers", { ...state.extraAnswers, [q.id]: e.target.value })} />
    </div>
  );
};

export const AdditionalQuestions = ({ questions }) => (
  <div>
    <h2 className="font-heading text-lg font-semibold mb-3">Additional questions for patient & physician</h2>
    <div className="border rounded-md bg-card divide-y" data-testid="additional-questions">
      {withKeys(questions, (q) => q.id || q.question).map(([key, q], i) => <AdditionalQuestion key={key} q={q} i={i} />)}
    </div>
  </div>
);

const InvestigationRow = ({ inv, i, checked, onToggle }) => (
  <label className="p-4 flex gap-3 items-start cursor-pointer hover:bg-muted/50" data-testid={`investigation-${i}`}>
    <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`investigation-${i}-checkbox`} className="mt-0.5" />
    <div className="flex-1">
      <div className="flex flex-wrap gap-2 items-center"><span className="font-medium">{inv.name}</span>
        <span className={`cc-chip ${PR[inv.priority] || ""}`}>{inv.priority}</span>
        <span className="text-xs text-muted-foreground">{inv.category}</span></div>
      <div className="text-sm text-muted-foreground mt-0.5">{inv.rationale}</div>
    </div>
  </label>
);

export const InvestigationPicker = ({ investigations = [] }) => {
  const { state, set } = useSession();
  const sel = state.selectedInv;
  const toggle = (name) => set("selectedInv", sel.includes(name) ? sel.filter((x) => x !== name) : [...sel, name]);
  return (
    <div>
      <h2 className="font-heading text-lg font-semibold mb-3">Recommended investigations <span className="text-sm font-normal text-muted-foreground">({sel.length} selected)</span></h2>
      <div className="border rounded-md bg-card divide-y" data-testid="investigations-list">
        {withKeys(investigations, (inv) => inv.name).map(([key, inv], i) => (
          <InvestigationRow key={key} inv={inv} i={i} checked={sel.includes(inv.name)} onToggle={() => toggle(inv.name)} />
        ))}
      </div>
    </div>
  );
};
